import { z } from 'zod';
import { schoolGroupSchema, timestampSchema } from './common';
import { difficultySchema, questionTypeSchema } from './content';

export const STATS_PERIODS = [7, 30, 90] as const;

export const statsQuerySchema = z.object({
  days: z.coerce.number().pipe(z.literal(STATS_PERIODS)).default(30),
});

const metricSchema = z.object({ current: z.number(), previous: z.number() });

export const questionStandoutSchema = z.object({
  id: z.uuid(),
  prompt: z.string(),
  type: questionTypeSchema,
  schoolGroup: schoolGroupSchema,
  subjectName: z.string(),
  difficulty: difficultySchema,
  thumbnailKey: z.string().nullable(),
  answered: z.int().min(0),
  correctRate: z.number().min(0).max(1),
  verdict: z.enum(['too_hard', 'too_easy']),
});

export const adminStatsSchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  days: z.int(),
  metrics: z.object({
    activeChildren: metricSchema,
    rounds: metricSchema,
    newFamilies: metricSchema,
    cancellations: metricSchema,
    revenueCents: metricSchema,
    goalsReached: metricSchema,
  }),
  daily: z.array(
    z.object({
      day: z.iso.date(),
      rounds: z.int().min(0),
      activeChildren: z.int().min(0),
      newFamilies: z.int().min(0),
      goalsReached: z.int().min(0),
      cancellations: z.int().min(0),
      revenueCents: z.int().min(0),
    }),
  ),
  subjects: z.array(
    z.object({
      subjectKey: z.string(),
      name: z.string(),
      rounds: z.int().min(0),
      accuracy: z.number().nullable(),
    }),
  ),
  groups: z.array(
    z.object({
      schoolGroup: schoolGroupSchema,
      activeChildren: z.int().min(0),
      rounds: z.int().min(0),
      accuracy: z.number().nullable(),
    }),
  ),
  families: z.object({
    active: z.int().min(0),
    cancelled: z.int().min(0),
    failed: z.int().min(0),
    expired: z.int().min(0),
    none: z.int().min(0),
  }),
  standouts: z.array(questionStandoutSchema),
});

export const questionStatsSchema = z.object({
  answered: z.int().min(0),
  correct: z.int().min(0),
  hinted: z.int().min(0),
  recentAnswered: z.int().min(0),
  recentCorrect: z.int().min(0),
  lastAnsweredAt: timestampSchema.nullable(),
  commonMistakes: z.array(z.object({ answer: z.string(), count: z.int().min(1) })),
});

export type StatsPeriod = (typeof STATS_PERIODS)[number];
export type AdminStats = z.infer<typeof adminStatsSchema>;
export type QuestionStandout = z.infer<typeof questionStandoutSchema>;
export type QuestionStats = z.infer<typeof questionStatsSchema>;

export const adminSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(100),
});

export const adminSearchSchema = z.object({
  questions: z.array(
    z.object({
      id: z.uuid(),
      prompt: z.string(),
      type: questionTypeSchema,
      schoolGroup: schoolGroupSchema,
      subjectName: z.string(),
      thumbnailKey: z.string().nullable(),
    }),
  ),
  readingTexts: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      schoolGroup: schoolGroupSchema,
      imageKey: z.string().nullable(),
    }),
  ),
  families: z.array(
    z.object({
      id: z.uuid(),
      email: z.string(),
      name: z.string().nullable(),
      photoUrl: z.string().nullable(),
    }),
  ),
  billing: z.array(
    z.object({
      userId: z.uuid(),
      email: z.string(),
      reference: z.string(),
      kind: z.enum(['order', 'payment', 'customer']),
    }),
  ),
});

export type AdminSearch = z.infer<typeof adminSearchSchema>;
